import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  UpsertBusinessFromExcelApi,
  UpsertBusinessFromExcelPostRequest,
  UpsertBusinessFromExcelResponse,
} from '../axios/generated/registerBusinessManage/api';
import { IUpsertBusinessesRepository } from '../interfaces/IUpsertBusinessesRepository';
import { IError } from '../interfaces/IError';
import { customAxiosInstance } from '../axios/axiosInstance';
import { axiosErrorHandler } from '../axios/axiosError';
import { pollAsyncProcess } from '../composables/useAsyncPolling';

/**
 * Axiosを使ったリポジトリ実装例
 */
export class UpsertBusinessesRepository implements IUpsertBusinessesRepository {
  /**
   * 集計情報の登録
   * とりあえず、bulkは考えず単体の1取引分の情報をputする
   * @param upsertBusinessFromExcelPostRequest - 更新するビジネス情報
   */
  async put(
    upsertBusinessFromExcelPostRequest: UpsertBusinessFromExcelPostRequest
  ): Promise<INetworkResult<UpsertBusinessFromExcelResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const upsertBusinessFromExcelApi = new UpsertBusinessFromExcelApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await upsertBusinessFromExcelApi.upsertBusinessFromExcelPost(upsertBusinessFromExcelPostRequest);
      if (apiResponse.status !== 200) {
        throw new Error('想定外のレスポンス');
      }

      const bodyStatusCode = apiResponse.data.statusCode;
      if (bodyStatusCode === 202) {
        const asyncResult = apiResponse.data.result as unknown as { requestId?: string };
        if (!asyncResult.requestId) {
          throw new Error('非同期レスポンスにrequestIdが含まれていません');
        }

        const pollingResult = await pollAsyncProcess<UpsertBusinessFromExcelResponse['result']>(asyncResult.requestId, {
          actionType: 'upsert-business-from-excel',
        });

        if (pollingResult.status === 'failed') {
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 500,
                code: 'INTERNAL_SERVER_ERROR',
                message: pollingResult.errorMessage ?? '取引登録処理が失敗しました',
              },
            },
          };
        }

        if (!pollingResult.result) {
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 500,
                code: 'INTERNAL_SERVER_ERROR',
                message: '非同期処理の結果が取得できませんでした',
              },
            },
          };
        }

        const asyncResponse: UpsertBusinessFromExcelResponse = {
          statusCode: 200,
          code: 200,
          message: 'success',
          result: pollingResult.result,
        };
        return { status: { type: 'success', data: asyncResponse } };
      }

      const result: UpsertBusinessFromExcelResponse = apiResponse.data;
      return { status: { type: 'success', data: result } };
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
